import Container from "@/components/Container/Container";
import Layout from "@/components/Layout/Layout";
import { Heading, H6Css, Body, H5Css, H4Css } from "@/components/Typography";
import { mq } from "@/constants/breakpoints";
import { css } from "@emotion/react";
import { GetStaticProps } from "next";
import BottomCTA from "@/components/BottomCTA/BottomCTA";
import { gotham } from "@/utils/fonts";
import {
  darkColor,
  lightBlueColor,
  mutedBlueColor,
  textColor,
} from "@/constants/colors";
import { contentfulClient } from "@/utils/contentful";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import options from "@/utils/richTextOptions";
import { BLOCKS } from "@contentful/rich-text-types";
import { ResponsiveNextImage } from "@/components/ResponsiveNextImage";

const IMAGE_DIMENSIONS = {
  capterra: {
    xs: { width: 57.62, height: 52.77 },
    sm: { width: 65.85, height: 60.31 },
    lg: { width: 107, height: 98 },
  },
  g2: {
    xs: { width: 50.08, height: 56 },
    sm: { width: 57.23, height: 64 },
    lg: { width: 93, height: 104 },
  },
  softwareAdvice: {
    xs: { width: 52.77, height: 56 },
    sm: { width: 60.31, height: 64 },
    lg: { width: 98, height: 104 },
  },
  trustpilot: {
    xs: { width: 102.8, height: 51.69 },
    sm: { width: 117.49, height: 59.08 },
    lg: { width: 190.92, height: 96 },
  },
} as const;

const Page = ({ fields }: any) => {
  return (
    <Layout
      title={fields.pageTitle}
      metaDescription={fields.metaDescription}
      heroContainerCss={css`
        background: url(/images/customer-care-commitment/BG.svg),
          linear-gradient(283.92deg, #048cdd -16.54%, #32b2ff 100.04%);
        background-size: cover;
        background-repeat: no-repeat;
      `}
      hero={
        <Container>
          <Heading
            level={1}
            css={css`
              color: #fff;
              text-align: center;
              padding: 60px 0;
              max-width: 800px;
              margin: 0 auto;
              font-size: 28px;
              font-weight: 400;
              ${mq["lg"]} {
                font-size: 56px;
              }
            `}
          >
            {fields.heroHeading}
          </Heading>
        </Container>
      }
    >
      <Container
        css={css`
          margin: auto;
          padding-left: 16px;
          padding-right: 16px;
          ${mq["sm"]} {
            padding-left: 40px;
            padding-right: 40px;
          }
        `}
      >
        <div
          css={css`
            background: ${mutedBlueColor};
            border-radius: 15px;
            padding: 20px 24px;
            margin: 30px 0px;

            ${mq["md"]} {
              padding: 30px 40px;
            }
            ${mq["lg"]} {
              margin: 112px auto;
              max-width: 800px;
              padding: 60px 100px;
            }
          `}
        >
          <Body
            css={css`
              color: ${textColor};
              font-weight: 400;
              font-size: 14px;
              line-height: 19.6px;
              margin-bottom: 24px;
            `}
          >
            {documentToReactComponents(fields.customerCareCommitmentPoints, {
              renderNode: {
                ...options().renderNode,

                paragraph: (node, children) => (
                  <>
                    {children}
                    <br />
                  </>
                ),
                [BLOCKS.HEADING_4]: (node: any, children: any) => (
                  <Heading
                    level={4}
                    css={css`
                      ${H4Css}
                      margin-top: 36px;
                      font-weight: 700;
                      font-size: 24px;
                      ${mq["lg"]} {
                        margin-top: 50px;
                        font-size: 32px;
                      }
                      color: ${lightBlueColor};
                    `}
                  >
                    {children}
                  </Heading>
                ),
                [BLOCKS.HEADING_6]: (node: any, children: any) => (
                  <Heading
                    level={6}
                    css={css`
                      ${H6Css}
                      margin-top: 24px;
                      font-weight: 700;
                      font-size: 18px;
                      ${mq["lg"]} {
                        margin-top: 30px;
                        font-size: 24px;
                      }
                      color: ${darkColor};
                    `}
                  >
                    {children}
                  </Heading>
                ),
              },
            })}
          </Body>
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 20px;
            margin-top: 60px;
            ${mq["md"]} {
              flex-direction: row;
              gap: 0px;
              margin-top: 63px;
            }
            ${mq["lg"]} {
              flex-direction: column;
              gap: 20px;
              margin-top: 120px;
            }
            ${mq["xl"]} {
              margin-top: 125px;
            }
            align-items: center;
          `}
        >
          <Heading
            level={5}
            css={css`
              ${H5Css}
              font-weight: 700;
              color: ${darkColor};
              text-align: center;
              width: 100%;
              ${mq["md"]} {
                margin-bottom: 0;
                margin-right: 40px;
              }
              ${mq["lg"]} {
                font-size: 50px;
              }
            `}
          >
            {fields.customerCareStatsHeading}
          </Heading>

          <div
            css={css`
              display: flex;
              justify-content: center;
              gap: 14px;
              flex-wrap: wrap;
              ${mq["md"]} {
                gap: 40px;
                flex-wrap: nowrap;
              }
            `}
          >
            {fields.customerCareBadges.map(({ fields }: any) => {
              const dimensions =
                IMAGE_DIMENSIONS[fields.title as keyof typeof IMAGE_DIMENSIONS];
              const src = `https:${fields.file.url}`;
              //   return null;
              return (
                <ResponsiveNextImage
                  key={fields.description}
                  images={[
                    { src, screenSize: "xs", ...dimensions.xs },
                    { src, screenSize: "sm", ...dimensions.sm },
                    { src, screenSize: "lg", ...dimensions.lg },
                  ]}
                  alt={fields.description}
                  priority
                />
              );
            })}
          </div>
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            border: 2px solid #daf2ff;
            border-radius: 15px;
            padding: 30px;
            margin-top: 40px;

            ${mq["md"]} {
              margin-top: 33px;
              flex-direction: row;
              justify-content: space-between;
              flex-wrap: wrap;
              align-items: start;
            }
            ${mq["lg"]} {
              flex-direction: row;
              justify-content: center;
              align-items: start;
              margin-top: 60px;
            }
            ${mq["xl"]} {
              margin-top: 65px;
              flex-direction: row;
              flex-wrap: nowrap;
              align-items: center;
            }
            gap: 50px;
          `}
        >
          {fields.customerCareStats.map((stat: any, index: number) => (
            <div
              key={index}
              css={css`
                text-align: center;
                gap: 8px;
                max-width: 300px;
              `}
            >
              <Heading
                level={6}
                css={css`
                  ${H6Css}
                  color: ${lightBlueColor};
                  font-weight: 400;
                  line-height: normal;
                  font-size: ${index === 0 ? "24px" : "40px"};
                  margin-bottom: 10px;
                `}
              >
                {stat.fields.heading}
              </Heading>
              <Body
                css={css`
                  ${gotham.style};
                  color: ${darkColor};
                  font-weight: 400;
                  font-size: 14px;
                  line-height: 19.04px;
                  ${mq["sm"]} {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  }
                `}
              >
                {stat.fields.description}
              </Body>
            </div>
          ))}
        </div>
      </Container>

      <div
        css={css`
          margin-top: 60px;
          ${mq["lg"]} {
            margin-top: 120px;
          }
        `}
      >
        <BottomCTA />
      </div>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const entries = await contentfulClient.getEntries({
    content_type: "customerCareCommitmentPage",
  });
  const fields = entries.items[0].fields;
  return {
    props: { fields },
  };
};

export default Page;
